import { Component, ElementRef, Input, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { NGXLogger as LoggerService } from 'ngx-logger';
import { AdFreeService } from '../ad-free.service';
import { AdvertisingService } from '../advertising.service';
import { GoogleAnalyticsService } from '../google-analytics.service';
import { ResponsiveService } from '../responsive.service';
import { UIMesageService } from '../ui-message.service';
import { RewardedAdProvider } from './rewarded-ad.types';

/** CTA has to be at least half in the viewport for at least this long to count as seen. */
const IMPRESSION_VISIBLE_RATIO = 0.5;
const IMPRESSION_DWELL_MS = 1000;

/**
 * Permanent CTA affordance at the freed-up ex-Akcelo slot — and the single
 * entry point into the rewarded flow.
 *
 * There is deliberately no dialog in between (see handover §13): the AppLixir
 * v6 player always opens as its OWN fixed fullscreen modal, so any dialog of
 * ours would just sit behind it as an empty white box. Instead the button
 * itself turns into a "Loading ad…" state until the provider's overlay takes
 * over, and the result is handled right here:
 *   complete            -> grant the ad-free hour (CTA disappears via gating)
 *   error/adblocked/no-ad -> friendly toast (we own feedback; the SDK's own
 *                            toast is disabled), CTA back to idle
 *   skipped/closed      -> user exited knowingly, CTA back to idle, no toast
 *
 * Visible only when this viewer actually sees ads and no ad-free hour is
 * running — which is exactly `advertisingService.fuseAdsVisible` (it already folds
 * in the premium-viewer / premium-owner-room and ad-free checks). Shown to every
 * ad-seeing user (owner AND participant) — NOT gated on `!isOwner` like the old
 * Akcelo block. See handover §5 (visibility warning) / §6.
 *
 * GA4 funnel (handover-rewarded-cta-funnel-tracking-2026-08-23):
 * `rewarded_cta_impression` -> `rewarded_cta_click` -> `rewarded_ad_result`
 * -> `rewarded_ad_free_granted`, each split by `screen_size` / `user_role`.
 * The impression is deliberately viewport-based and NOT derived from `visible`:
 * `visible` only says the CTA was rendered, and on small screens it sits at the
 * bottom of `poker-room.page.html` where it may never come into view at all.
 */
@Component({
    selector: 'rewarded-ad-cta',
    templateUrl: './rewarded-ad-cta.component.html',
    styleUrls: ['./rewarded-ad-cta.component.scss'],
})
export class RewardedAdCtaComponent implements OnDestroy {
    /** Set by the embedding page; omitted the parameter is simply left off the events. */
    @Input() userRole: 'owner' | 'participant';

    loading = false;

    private observer: IntersectionObserver = null;
    private dwellTimeout: ReturnType<typeof setTimeout> = null;
    private hasTrackedImpression = false;

    constructor(
        public advertisingService: AdvertisingService,
        private provider: RewardedAdProvider,
        private adFreeService: AdFreeService,
        private uiMessage: UIMesageService,
        private responsiveService: ResponsiveService,
        private googleAnalyticsService: GoogleAnalyticsService,
        private logger: LoggerService,
        private zone: NgZone,
    ) {}

    get visible(): boolean {
        return this.advertisingService.fuseAdsVisible;
    }

    /**
     * Setter rather than a plain `@ViewChild`, because the element only comes
     * into existence with the `*ngIf="visible"` — and vanishes again when the
     * ad-free hour starts.
     */
    @ViewChild('ctaRoot')
    set ctaRoot(element: ElementRef<HTMLElement>) {
        this.disconnectObserver();
        if (element && !this.hasTrackedImpression) {
            this.observeImpression(element.nativeElement);
        }
    }

    ngOnDestroy(): void {
        this.disconnectObserver();
    }

    async startRewardedAd(): Promise<void> {
        if (this.loading) {
            return; // double-click guard; the button is disabled anyway
        }
        this.googleAnalyticsService.event('rewarded_cta_click', this.trackingParams());
        this.loading = true;
        try {
            const result = await this.provider.showRewardedAd({
                // The flow already settled via watchdog timeout (e.g. the user sat
                // on the consent dialog, then accepted and watched the full ad):
                // grant after all and correct the error toast shown at timeout.
                onLateComplete: () =>
                    this.zone.run(() => {
                        this.logger.log('RewardedAdCta: late complete — granting after timeout');
                        this.adFreeService.grantAdFree();
                        this.googleAnalyticsService.event('rewarded_ad_free_granted', {
                            ...this.trackingParams(),
                            grant_path: 'late_complete',
                        });
                        this.uiMessage.success($localize`:@@rewardedAdLateGrantToast:Thanks for watching — ads are now off.`);
                    }),
            });
            // Provider callbacks (AppLixir SDK) resolve outside Angular's zone —
            // run the result handling in-zone so the bindings re-evaluate.
            this.zone.run(() => {
                this.logger.log('RewardedAdCta: result', result.status);
                this.googleAnalyticsService.event('rewarded_ad_result', {
                    ...this.trackingParams(),
                    status: result.status,
                });
                switch (result.status) {
                    case 'complete':
                        this.adFreeService.grantAdFree();
                        this.googleAnalyticsService.event('rewarded_ad_free_granted', {
                            ...this.trackingParams(),
                            grant_path: 'direct',
                        });
                        break;
                    case 'error':
                    case 'adblocked':
                    case 'no-ad':
                        this.uiMessage.warning($localize`:@@rewardedAdErrorToast:The video could not be loaded. Please try again later.`);
                        break;
                    // 'skipped' | 'closed' -> user exited the player, no grant, no toast
                }
            });
        } catch (error) {
            this.logger.error('RewardedAdCta: showRewardedAd failed', error);
            // The provider contract says it never rejects — if it does anyway, the
            // funnel would silently lose those attempts between click and result.
            this.googleAnalyticsService.event('rewarded_ad_result', {
                ...this.trackingParams(),
                status: 'error',
            });
        } finally {
            this.zone.run(() => (this.loading = false));
        }
    }

    /**
     * Counts one impression per page view: the CTA has to sit at least half in
     * the viewport for a full second, and a re-appearing CTA (after the ad-free
     * hour ran out) is not counted again — otherwise the impression number stops
     * being a count of distinct chances to see the offer.
     */
    private observeImpression(element: HTMLElement): void {
        this.observer = new IntersectionObserver(
            (entries) => {
                const seen = entries[entries.length - 1].intersectionRatio >= IMPRESSION_VISIBLE_RATIO;
                if (seen && this.dwellTimeout === null) {
                    this.dwellTimeout = setTimeout(() => this.trackImpression(), IMPRESSION_DWELL_MS);
                } else if (!seen) {
                    this.clearDwellTimeout(); // scrolled away again before the second was up
                }
            },
            { threshold: IMPRESSION_VISIBLE_RATIO },
        );
        this.observer.observe(element);
    }

    private trackImpression(): void {
        this.dwellTimeout = null;
        this.hasTrackedImpression = true;
        this.disconnectObserver();
        this.googleAnalyticsService.event('rewarded_cta_impression', this.trackingParams());
    }

    private trackingParams(): Record<string, string> {
        const params: Record<string, string> = {
            screen_size: this.responsiveService.isScreenSmall() ? 'small' : 'large',
        };
        if (this.userRole) {
            params.user_role = this.userRole;
        }
        return params;
    }

    private disconnectObserver(): void {
        this.clearDwellTimeout();
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
    }

    private clearDwellTimeout(): void {
        if (this.dwellTimeout !== null) {
            clearTimeout(this.dwellTimeout);
            this.dwellTimeout = null;
        }
    }
}
